import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import i18n from '@/i18n';
import {
  AlertTriangle,
  Bell,
  Boxes,
  Bot,
  Coins,
  FileText,
  Gauge,
  ShieldCheck,
  GitBranch,
  Grid3x3,
  Home,
  Inbox,
  LayoutDashboard,
  Layers,
  Lock,
  LogOut,
  Menu,
  MessageSquare,
  Package,
  Settings,
  SlidersHorizontal,
  TrendingUp,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';

import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { MailUndeliverableBanner } from '@/components/MailUndeliverableBanner';
import { TrialBar } from '@/components/TrialBar';
import { useLookAlong } from '@/lib/impersonation';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ConnectAuthorizePage } from '@/pages/auth/ConnectAuthorizePage';
import { AppFooter } from '@/components/AppFooter';
import { MeldKnop } from '@/components/melden/MeldKnop';
import { FirstRunProfile } from '@/components/FirstRunProfile';
import { TermsGate } from '@/components/TermsGate';
import { RequireOperator } from '@/components/RequireOperator';
import { cn } from '@/lib/cn';
import { logout } from '@/lib/auth';

import { SkeletonList, SkeletonTiles } from '@/components/Skeleton';
import { Toaster } from '@/components/ui/Toaster';
import { openBadge } from '@/components/pricing/repricingFrame';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMarket } from '@/hooks/useMarket';
import { useRealtime } from '@/hooks/useRealtime';
import { useTheme } from '@/hooks/useTheme';
import { useAccountOverlay } from '@/stores/accountOverlay';
import { AccountOverlay } from '@/pages/settings/AccountOverlay';
import { api } from '@/lib/api';
import { SUPPORT_EMAIL } from '@/lib/support';

// Per-route code-splitting: each page-bundle is fetched only when the user
// navigates to it. Cuts the initial JS payload roughly in half — Recharts,
// Radix-Select etc. don't ship with the dashboard load.
const MachinePage = lazy(() =>
  import('@/pages/admin/machine/MachinePage').then((m) => ({ default: m.MachinePage })),
);
const AdminAgentsPage = lazy(() =>
  import('@/pages/AdminAgentsPage').then((m) => ({ default: m.AdminAgentsPage })),
);
const FacetApprovalPage = lazy(() =>
  import('@/pages/admin/FacetApprovalPage').then((m) => ({ default: m.FacetApprovalPage })),
);
const CustomersPage = lazy(() =>
  import('@/pages/admin/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const SignupsPage = lazy(() =>
  import('@/pages/admin/SignupsPage').then((m) => ({ default: m.SignupsPage })),
);
const LadderPage = lazy(() =>
  import('@/pages/admin/LadderPage').then((m) => ({ default: m.LadderPage })),
);
const ProblemsPage = lazy(() =>
  import('@/pages/admin/ProblemsPage').then((m) => ({ default: m.ProblemsPage })),
);
const TodayPage = lazy(() =>
  import('@/pages/admin/TodayPage').then((m) => ({ default: m.TodayPage })),
);
const CustomerDetailPage = lazy(() =>
  import('@/pages/admin/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const SupportInboxPage = lazy(() =>
  import('@/pages/admin/SupportInboxPage').then((m) => ({ default: m.SupportInboxPage })),
);
const SupportTicketPage = lazy(() =>
  import('@/pages/admin/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })),
);
const CatalogPage = lazy(() =>
  import('@/pages/catalog/CatalogPage').then((m) => ({ default: m.CatalogPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
// V2: MatchQueuePage removed (Fase 2). Replaced by MappingReviewPage.
// Bronnen-redesign: standalone Mappings/Retailers pages consolidated into the
// Sources workspace (3 tabs). Old routes redirect to /sources.
const SourcesPage = lazy(() =>
  import('@/pages/sources/SourcesPage').then((m) => ({ default: m.SourcesPage })),
);
const MappingReviewPage = lazy(() =>
  import('@/pages/MappingReviewPage').then((m) => ({ default: m.MappingReviewPage })),
);
const OverviewPage = lazy(() =>
  import('@/pages/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
const OverviewCellPage = lazy(() =>
  import('@/pages/OverviewCellPage').then((m) => ({ default: m.OverviewCellPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const MijnMeldingenPage = lazy(() =>
  import('@/pages/MijnMeldingenPage').then((m) => ({ default: m.MijnMeldingenPage })),
);
const TrendsPage = lazy(() => import('@/pages/TrendsPage').then((m) => ({ default: m.TrendsPage })));
const AlertsPage = lazy(() => import('@/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const RepricingPage = lazy(() =>
  import('@/pages/RepricingPage').then((m) => ({ default: m.RepricingPage })),
);
const DynamicPricingPage = lazy(
  () => import('@/pages/DynamicPricingPage').then((m) => ({ default: m.DynamicPricingPage })),
);
const StrategiePage = lazy(() =>
  import('@/pages/StrategiePage').then((m) => ({ default: m.StrategiePage })),
);
const ReportsPage = lazy(() =>
  import('@/pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
);
const LoginPage = lazy(() => import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const ClaimPage = lazy(() =>
  import('@/pages/auth/ClaimPage').then((m) => ({ default: m.ClaimPage })),
);
const AcceptInvitePage = lazy(() =>
  import('@/pages/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
);
const SignupPage = lazy(() => import('@/pages/auth/SignupPage').then((m) => ({ default: m.SignupPage })));
const VerifyPage = lazy(() => import('@/pages/auth/VerifyPage').then((m) => ({ default: m.VerifyPage })));
const LegalDocPage = lazy(() =>
  import('@/pages/legal/LegalDocPage').then((m) => ({ default: m.LegalDocPage })),
);
const CompanyDetailsPage = lazy(() =>
  import('@/pages/legal/CompanyDetailsPage').then((m) => ({ default: m.CompanyDetailsPage })),
);

/** Where a customer goes when something is wrong. Both live outside the app.
 *
 *  SUPPORT_EMAIL lives in lib/support.ts, shared with the auth pages.
 *
 *  STATUS_URL is empty until the public status page exists; the link renders
 *  only when it is filled, so nobody clicks through to nothing.
 *  ponytail: constants beat an env var — a new env var is an infra change,
 *  and these values change roughly never. */
const STATUS_URL = '';

/** The sidebar's count badge and word tag: a borderless grey pill. */
const NAV_PILL = 'ml-auto font-mono text-[10.5px] leading-none px-1.5 py-[3px] rounded-full bg-bg-tertiary text-text-secondary';
const NAV_PILL_HOT = 'ml-auto font-mono text-[10.5px] leading-none px-1.5 py-[3px] rounded-full bg-tone-warn-bg text-tone-warn border border-tone-warn-border';

type BadgeKey =
  | 'reviewQueue' | 'alertsUnread' | 'pricingOpen'
  | 'adminSupport' | 'adminProblems' | 'adminSignups' | 'adminBreakers' | 'adminUnlinked';

/** Amber = the count goes to zero by the operator's own action (an inbox).
 *  Grey = a plain count. */
const HOT_BADGES: ReadonlySet<BadgeKey> = new Set(['adminSupport', 'adminProblems', 'adminSignups', 'adminBreakers']);

type NavItem = {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  badgeKey?: BadgeKey;
  /** A fixed word tag after the label ("beta"), as an i18n key. */
  tagKey?: string;
  /** Gates the item on an entitlement -- present but locked when the
   *  customer's plan does not include it (see `has()` in useEntitlements). */
  feature?: 'price_proposals' | 'dynamic_pricing';
};

type NavMode = 'app' | 'beheer';
const NAV_MODE_KEY = 'priceq.nav_mode';

type NavGroup = { labelKey: string; items: NavItem[]; operatorOnly?: boolean };

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.group.monitor',
    items: [
      { to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard, end: true },
      { to: '/overview', labelKey: 'nav.overview', icon: Grid3x3 },
      // The daily digest links here. Without a menu item next to it the mail
      // was the only way in, which is how the page came to be deleted in July.
      { to: '/alerts', labelKey: 'nav.alerts', icon: Bell, badgeKey: 'alertsUnread' },
      { to: '/trends', labelKey: 'nav.trends', icon: TrendingUp },
      // Naast Trends, niet onder "actie": een rapport lees je, je doet er niets
      // mee. Stond eerst tussen Prijsadvies en Strategie en werd daar niet
      // gevonden — de product owner zocht het en kwam bij "bestaat het wel?".
      { to: '/reports', labelKey: 'nav.reports', icon: FileText },
    ],
  },
  {
    labelKey: 'nav.group.catalogue',
    items: [
      { to: '/products', labelKey: 'nav.products', icon: Package },
      { to: '/sources', labelKey: 'nav.sources', icon: Boxes, badgeKey: 'reviewQueue' },
    ],
  },
  {
    // Was "actie", which read as "promotion" to anyone who had not built it.
    // Everything that moves a price on its own lives here now.
    labelKey: 'nav.group.dynamicPricing',
    items: [
      { to: '/pricing', labelKey: 'nav.repricing', icon: Coins, feature: 'price_proposals', badgeKey: 'pricingOpen' },
      { to: '/strategie', labelKey: 'nav.strategie', icon: GitBranch, feature: 'price_proposals' },
      { to: '/dynamic-pricing', labelKey: 'nav.automation', icon: ShieldCheck, feature: 'dynamic_pricing', tagKey: 'automation.beta' },
    ],
  },
  {
    // Outflanq-intern. `operatorOnly` keeps the whole group out of a
    // customer's sidebar; the API refuses them anyway, but a menu item that
    // does nothing is a bug report in the making. Shown only in Beheer mode.
    labelKey: 'nav.group.adminCustomers',
    operatorOnly: true,
    items: [
      { to: '/admin', end: true, labelKey: 'nav.adminToday', icon: Home },
      // No pill: a customer count never goes to zero, so it is not work (spec §3).
      { to: '/admin/klanten', labelKey: 'nav.adminCustomers', icon: Users },
      { to: '/admin/support', labelKey: 'nav.adminSupport', icon: MessageSquare, badgeKey: 'adminSupport' },
      { to: '/admin/problemen', labelKey: 'nav.adminProblems', icon: AlertTriangle, badgeKey: 'adminProblems' },
      { to: '/admin/aanmeldingen', labelKey: 'nav.adminSignups', icon: Inbox, badgeKey: 'adminSignups' },
    ],
  },
  {
    labelKey: 'nav.group.adminMachine',
    operatorOnly: true,
    items: [
      { to: '/admin/machine', labelKey: 'nav.adminMachine', icon: Gauge, badgeKey: 'adminBreakers' },
      { to: '/admin/agents', labelKey: 'nav.adminAgents', icon: Bot },
      { to: '/admin/ladder', labelKey: 'nav.adminLadder', icon: Layers },
      { to: '/admin/facets', labelKey: 'nav.adminFacets', icon: SlidersHorizontal, badgeKey: 'adminUnlinked' },
    ],
  },
];

/** Shared styling for sidebar nav links. Horizontal padding is supplied by the
 *  caller so the same link can go icon-rail (centered, no padding) or full. */
function sideNavClass({ isActive }: { isActive: boolean }): string {
  return [
    'flex items-center gap-sm py-sm',
    'text-body-sm border-l-2 transition-colors duration-fast',
    'focus:outline-hidden focus-visible:bg-bg-tertiary',
    isActive
      ? 'text-text-primary border-accent bg-accent/5'
      : 'text-text-secondary border-transparent hover:text-text-primary hover:bg-bg-tertiary/50',
  ].join(' ');
}

function PageFallback() {
  return (
    <section className="px-lg py-xl space-y-lg">
      <SkeletonTiles count={2} />
      <SkeletonList rows={3} />
    </section>
  );
}

const ROUTE_LABELS: Record<string, string> = {
  '/': 'nav.dashboard',
  '/overview': 'nav.overview',
  '/products': 'nav.products',
  '/sources': 'nav.sources',
  '/retailers': 'nav.retailers',
  '/mapping/review': 'nav.mappingReview',
  '/retailers/add': 'onboardingRetailer.title',
  '/categories': 'nav.categories',
  '/alerts': 'nav.alerts',
  '/trends': 'nav.trends',
  '/pricing': 'nav.repricing',
  '/strategie': 'nav.strategie',
  '/dynamic-pricing': 'nav.automation',
  '/reports': 'nav.reports',
  '/settings': 'nav.settings',
  '/admin': 'nav.adminToday',
  '/admin/klanten': 'nav.adminCustomers',
  '/admin/support': 'nav.adminSupport',
  '/admin/problemen': 'nav.adminProblems',
  '/admin/aanmeldingen': 'nav.adminSignups',
  '/admin/machine': 'nav.adminMachine',
  '/admin/ladder': 'nav.adminLadder',
  '/admin/agents': 'nav.adminAgents',
  '/admin/facets': 'nav.adminFacets',
};

export function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      {/* Above every route, login included: an operator must never be able to
          forget which account they are looking at. */}
      <ImpersonationBanner />
      {/* Here rather than inside AppShell, which is inside ProtectedRoute: the
          "Meekijksessie verlopen" toast is raised at the moment the session
          stops being valid, and a Toaster that unmounts with the protected
          tree is a message with nowhere to land. */}
      <Toaster />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* The front door. Outside ProtectedRoute, obviously: somebody who can
            already log in has no use for it. */}
        <Route path="/signup" element={<SignupPage />} />
        {/* Where the link in the confirmation mail lands. Opening it is what
            creates the customer -- see backend/signup/confirm.py. */}
        <Route path="/verify/:token" element={<VerifyPage />} />
        {/* Public, and they have to be: handing the terms over is what makes
            them binding, and a login wall is the opposite of handing over. */}
        <Route path="/legal/voorwaarden" element={<LegalDocPage doc="av" titleKey="legal.terms" />} />
        <Route path="/legal/privacy" element={<LegalDocPage doc="privacy" titleKey="legal.privacy" />} />
        <Route
          path="/legal/verwerkersovereenkomst"
          element={<LegalDocPage doc="vwo" titleKey="legal.dpa" />}
        />
        <Route
          path="/legal/subverwerkers"
          element={<LegalDocPage doc="subverwerkers" titleKey="legal.subprocessors" />}
        />
        <Route path="/legal/bedrijfsgegevens" element={<CompanyDetailsPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        {/* PriceQ native format: /reset-password/:token (path param) */}
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        {/* Central-auth format: /reset?token=... (query param) */}
        <Route path="/reset" element={<ResetPasswordPage />} />
        {/* PriceQ native format: /accept-invite/:token (path param) */}
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        {/* Central-auth format: /accept-invite?token=... (query param) */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* A prepared trial account, handed over from the outreach mail. */}
        <Route path="/claim/:token" element={<ClaimPage />} />
        {/* AI-app consent (spec 2026-11-02 §4.1): behind the login and the terms,
            but outside the app shell -- the person came from Claude or ChatGPT
            and goes straight back there. */}
        <Route
          path="/connect/authorize"
          element={
            <ProtectedRoute>
              <TermsGate>
                <ConnectAuthorizePage />
              </TermsGate>
            </ProtectedRoute>
          }
        />
        {/* Two gates, in this order. Agreeing to republished terms comes
            before anything else the app shows (art. 6:234 BW); the four
            first-run questions come after, because answering them is only
            sensible once the agreement stands. Both fail open on an error:
            neither may be able to lock a paying customer out of his own app. */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <TermsGate>
                <FirstRunProfile>
                  <AppShell />
                </FirstRunProfile>
              </TermsGate>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

function AppShell() {
  const { theme } = useTheme();

  // Single app-wide SSE connection: server-push events invalidate the
  // relevant TanStack Query caches so views refresh without polling.
  useRealtime();

  // Apply persisted theme to <html data-theme=…> on mount + every change.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // The language chosen on this account, not just in this browser. Without
  // this, logging in on a second device silently reverts to whatever that
  // browser prefers — and the periodic report, which is sent by a job with no
  // browser at all, had no language to go on.
  //
  // Not during a look-along. GET /me/language resolves (tenant_id, user_id),
  // and in a look-along those belong to different organisations — the
  // customer's tenant, the operator's user — so it finds no row and answers
  // the default. Applying that constant wrote it to localStorage, which is
  // origin-wide and outlives the tab: one support view and the operator's own
  // PriceQ was in Dutch, everywhere, with nothing connecting the two events.
  // Skipping it also restores the operator's own language by itself when the
  // look-along is stopped, because the query enables again.
  const lookAlong = useLookAlong();
  const languageQuery = useQuery({
    queryKey: ['me-language'],
    queryFn: () => api.me.language(),
    staleTime: 5 * 60_000,
    enabled: !lookAlong,
    // A failure here must never keep the app from rendering: the screen falls
    // back to the browser's language, which is what it did before this existed.
    retry: false,
  });
  useEffect(() => {
    const stored = languageQuery.data?.language;
    if (stored && stored !== i18n.language?.split('-')[0]) {
      void i18n.changeLanguage(stored);
    }
  }, [languageQuery.data]);

  // Same key + staleTime as useMarket/DashboardPage: one request per 5 min for
  // the whole app, not one per call site.
  const tenantQuery = useQuery({
    queryKey: ['tenant-me'],
    queryFn: () => api.tenants.me(),
    staleTime: 5 * 60_000,
  });

  // The signed-in person: the sidebar needs is_operator, the mail banner email_bounced.
  const meQuery = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.auth.me(),
    staleTime: 60_000,
  });
  const isOperator = meQuery.data?.is_operator ?? false;

  // Sidebar pills for the operator pages. Only asked when the session IS an
  // operator: a customer would get a 403 and a red console on every page.
  const summaryQuery = useQuery({
    queryKey: ['admin', 'summary'],
    queryFn: () => api.adminOps.summary(),
    enabled: isOperator,
    refetchInterval: 60_000,
  });

  // Beheer / App. Remembered per browser; a first visit that lands on an
  // /admin URL starts in Beheer so the link the operator just used is in view.
  const location = useLocation();
  const [navMode, setNavModeState] = useState<NavMode>(() => {
    try {
      const stored = window.localStorage.getItem(NAV_MODE_KEY);
      if (stored === 'app' || stored === 'beheer') return stored;
    } catch { /* private mode: fall through */ }
    return location.pathname.startsWith('/admin') ? 'beheer' : 'app';
  });
  const setNavMode = (mode: NavMode) => {
    setNavModeState(mode);
    try { window.localStorage.setItem(NAV_MODE_KEY, mode); } catch { /* ignore */ }
  };

  // Gates the locked-menu-item indicator in the sidebar. Same query key as
  // TrialBar, so this is the cached answer rather than a third request.
  const entitlements = useEntitlements();
  // V2: review-queue count drives the sidebar badge for /mapping/review.
  // No poll: the count only moves when mapping runs (SSE mapping_progress /
  // mapping_completed invalidate it, see useRealtime) or when the customer
  // decides an item (ReviewTab/MappingReviewPage invalidate it themselves).
  const reviewQueueQuery = useQuery({
    queryKey: ['mapping-review', 'count'],
    queryFn: () => api.mappings.review.list(1, 0),
  });

  // Zolang de instap niet klaar is blijft hij vindbaar, ook als de klant de
  // regel op het dashboard heeft dichtgeklapt.
  const setupQuery = useQuery({
    queryKey: ['me', 'setup'],
    queryFn: () => api.me.setup(),
    // Four EXISTS probes -- cheap enough to refetch on every mount, so a step
    // finished elsewhere (e.g. adding a retailer) doesn't stay unticked in the
    // sidebar for up to a minute.
    staleTime: 0,
    refetchOnMount: 'always',
    // Must match DashboardPage's ['me','setup'] query: TanStack Query v5
    // shares one observer per key, and `retry` only binds on whichever
    // observer's fetch() starts first. A failure here must never keep the
    // dashboard stuck behind its skeleton, so both call sites need retry:
    // false or a cold boot can let this one win and reintroduce the stall.
    retry: false,
  });
  const s = setupQuery.data;
  // Own shop only counts for a customer who has one; see SetupChecklist.
  const setupOpen = s
    ? [s.products, s.retailers, s.mappings, ...(s.own_shop_required ? [s.own_shop] : [])]
        .filter((d) => !d).length
    : 0;

  // Sidebar badge for /alerts. No poll: the 06:30 scan fans out a
  // `data_refreshed` notification, which useRealtime turns into an
  // invalidate-everything — and deciding an alert on the page invalidates
  // this key itself.
  // Same key AlertsPage uses, so deciding an alert there drops the badge in
  // the same tick -- a separate key would leave the sidebar claiming work the
  // customer has just finished.
  const alertSummaryQuery = useQuery({
    queryKey: ['alerts-summary'],
    queryFn: () => api.alerts.summary(),
  });

  // Sidebar badge for /pricing: what is still to handle (see openBadge).
  // Same key as the Prijsadvies page, so deciding there moves the badge in
  // the same tick. Not asked for a plan without price proposals (402).
  const { home_country: country } = useMarket();
  const pricingSummaryQuery = useQuery({
    queryKey: ['pricing', 'review', 'summary', country],
    queryFn: () => api.pricing.review.summary(country),
    staleTime: 30_000,
    enabled: entitlements.canRequest('price_proposals'),
  });

  const s2 = summaryQuery.data;
  const badges = {
    reviewQueue: reviewQueueQuery.data?.total ?? 0,
    alertsUnread: alertSummaryQuery.data?.total_open ?? 0,
    pricingOpen: openBadge(pricingSummaryQuery.data),

    adminSupport: (s2?.support_open ?? 0) + (s2?.support_waiting ?? 0),
    adminProblems: s2?.problems_open ?? 0,
    adminSignups: s2?.signups_pending ?? 0,
    // null = unknown: no pill, never a false zero (StatusRibbon rule).
    adminBreakers: s2?.breakers_open ?? 0,
    adminUnlinked: s2?.categories_unlinked ?? 0,
  };

  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary md:grid md:grid-cols-[68px_1fr] lg:grid-cols-[240px_1fr]">
      <Sidebar
        tenantName={tenantQuery.data?.name ?? null}
        badges={badges}
        setupOpen={setupOpen}
        isOperator={isOperator}
        mode={navMode}
        onModeChange={setNavMode}
        hasFeature={entitlements.has}
      />
      <MobileNav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        tenantName={tenantQuery.data?.name ?? null}
        badges={badges}
        setupOpen={setupOpen}
        isOperator={isOperator}
        mode={navMode}
        onModeChange={setNavMode}
        hasFeature={entitlements.has}
      />
      <main id="main" className="flex flex-col min-w-0">
        <TopBar onOpenNav={() => setNavOpen(true)} />
        {/* Above the page, not on one: the mail we cannot deliver is the daily
            digest and the periodic report, so there is no single screen where
            this belongs. `auth-me` is already fetched here for the sidebar. */}
        <MailUndeliverableBanner show={meQuery.data?.email_bounced ?? false} />
        {/* Under the top bar rather than above the whole app: it is the
            customer's own business, not an operator warning, and it must not
            look like the amber look-along band. */}
        <TrialBar />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/overview/:skuId/:retailerId" element={<OverviewCellPage />} />
            <Route path="/products" element={<CatalogPage />} />
            <Route path="/sources" element={<SourcesPage />} />
            {/* Bronnen-redesign: legacy retailers/add/mappings pages folded into
                the /sources workspace; old routes redirect there. */}
            <Route path="/retailers" element={<Navigate to="/sources" replace />} />
            <Route path="/retailers/add" element={<Navigate to="/sources" replace />} />
            <Route path="/mapping" element={<Navigate to="/sources" replace />} />
            <Route path="/mapping/review" element={<MappingReviewPage />} />
            {/* Catalogus-redesign: /categories is merged into /products. */}
            <Route path="/categories" element={<Navigate to="/products" replace />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/trends" element={<TrendsPage />} />
            <Route path="/pricing" element={<RepricingPage />} />
            <Route path="/strategie" element={<StrategiePage />} />
            <Route path="/dynamic-pricing" element={<DynamicPricingPage />} />
            {/* Renamed from /automaat -- old bookmarks and links still work. */}
            <Route path="/automaat" element={<Navigate to="/dynamic-pricing" replace />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            {/* Klant-feedback-lus P7; also the link in Qontrol's customer mails (deel 12). */}
            <Route path="/meldingen" element={<MijnMeldingenPage />} />
            {/* Outflanq-intern — staat in NAV_GROUPS onder `operatorOnly` */}
            <Route
              path="/admin"
              element={
                <RequireOperator>
                  <TodayPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/klanten"
              element={
                <RequireOperator>
                  <CustomersPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/klanten/:tenantId"
              element={
                <RequireOperator>
                  <CustomerDetailPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/support"
              element={
                <RequireOperator>
                  <SupportInboxPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/support/:ticketId"
              element={
                <RequireOperator>
                  <SupportTicketPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/aanmeldingen"
              element={
                <RequireOperator>
                  <SignupsPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/problemen"
              element={
                <RequireOperator>
                  <ProblemsPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/ladder"
              element={
                <RequireOperator>
                  <LadderPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/agents"
              element={
                <RequireOperator>
                  <AdminAgentsPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/facets"
              element={
                <RequireOperator>
                  <FacetApprovalPage />
                </RequireOperator>
              }
            />
            <Route
              path="/admin/machine"
              element={
                <RequireOperator>
                  <MachinePage />
                </RequireOperator>
              }
            />
          </Routes>
        </Suspense>
        {/* Inside <main>, which is the flex column: as a sibling it would land
            in the next cell of the sidebar grid. `mt-auto` pins it to the
            bottom on a short page. */}
        <AppFooter />
      </main>

      <AccountOverlayHost />
      {/* Klant-feedback-lus: hides itself without vrijgave or in a look-along. */}
      <MeldKnop />
    </div>
  );
}

/** Eén overlay voor de hele app: het menu-item én een zoekresultaat op de
 *  instellingen-pagina openen dezelfde. */
function AccountOverlayHost() {
  const open = useAccountOverlay((st) => st.open);
  const section = useAccountOverlay((st) => st.section);
  const setOpen = useAccountOverlay((st) => st.setOpen);
  return <AccountOverlay open={open} onOpenChange={setOpen} section={section} />;
}

function Sidebar({
  tenantName,
  badges,
  setupOpen,
  isOperator,
  mode,
  onModeChange,
  hasFeature,
  expanded = false,
  onNavigate,
}: {
  tenantName: string | null;
  badges: Record<BadgeKey, number>;
  /** Steps still open in the getting-started checklist; 0 hides the footer link. */
  setupOpen: number;
  /** Whether the current session is Outflanq staff — gates `operatorOnly` groups. */
  isOperator: boolean;
  /** Operator only: which sidebar is showing. Customers always get 'app'. */
  mode: NavMode;
  onModeChange: (mode: NavMode) => void;
  /** Whether the customer's plan includes a `NavItem.feature` — locked items
   *  stay clickable but show a lock icon. */
  hasFeature: (feature: 'price_proposals' | 'dynamic_pricing') => boolean;
  /** Mobile drawer forces the full (labelled) layout regardless of breakpoint. */
  expanded?: boolean;
  /** Fired on any nav click — the mobile drawer uses it to close itself. */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const showAccount = useAccountOverlay((st) => st.show);
  const effectiveMode: NavMode = isOperator ? mode : 'app';
  const groups = NAV_GROUPS.filter((g) =>
    effectiveMode === 'beheer' ? g.operatorOnly === true : !g.operatorOnly,
  );

  // Collapsed = icon-rail at md → full sidebar at lg. The mobile drawer passes
  // `expanded` so labels/headers always show there.
  const labelCls = expanded ? 'flex-1' : 'flex-1 hidden lg:block';
  const groupCls = expanded ? 'block' : 'hidden lg:block';
  const badgeCls = expanded ? 'inline-block' : 'hidden lg:inline-block';
  // Wordmark: the "price" half hides on the icon rail, the red q always shows.
  const wordmarkCls = expanded ? 'inline' : 'hidden lg:inline';
  const padCls = expanded ? 'px-xl' : 'justify-center px-0 lg:justify-start lg:px-xl';
  const linkCls = ({ isActive }: { isActive: boolean }) => cn(sideNavClass({ isActive }), padCls);
  // The icon rail's one-button mode switch.
  const nextMode: NavMode = effectiveMode === 'app' ? 'beheer' : 'app';
  const ModeIcon = effectiveMode === 'app' ? Home : LayoutDashboard;
  const nextModeLabel = t('nav.modeSwitchTo', { mode: t(nextMode === 'app' ? 'nav.modeApp' : 'nav.modeBeheer') });

  return (
    <aside
      aria-label={t('common.navigation')}
      className={cn(
        'sticky top-0 h-screen border-r border-border-subtle bg-bg-secondary flex-col py-lg',
        expanded ? 'flex w-[264px]' : 'hidden md:flex',
      )}
    >
      {/* Wordmark, not a logo tile: lowercase, Inter Medium, the final q in
          brand red. That q is the only place the brand asserts itself — a
          coloured block would break the design language. Collapsed to the
          icon rail, the lone red q is the mark. */}
      <div className={cn('flex items-baseline gap-sm pb-lg border-b border-border-subtle', padCls)}>
        <span
          className="font-wordmark font-medium text-text-primary shrink-0"
          style={{ fontSize: '19px', letterSpacing: '-0.02em' }}
        >
          {/* The name is split for the red q and hidden down to a single letter
              on the icon rail, so the readable name is carried separately. */}
          <span className="sr-only">PriceQ</span>
          <span aria-hidden>
            <span className={wordmarkCls}>price</span>
            <span className="text-brand-mark">q</span>
          </span>
        </span>
        <div className={cn('min-w-0', groupCls)}>
          <p data-meld-geheim className="text-caption text-text-muted truncate">{tenantName ?? '…'}</p>
        </div>
      </div>

      {isOperator && (
        <div
          role="group"
          aria-label={t('nav.modeSwitch')}
          // Not `groupCls`: its `lg:block` outranks `grid` in Tailwind's cascade and
          // packed the two buttons to the left (issue #600).
          className={cn('mx-lg mt-md grid-cols-2 rounded-sm border border-border text-caption', expanded ? 'grid' : 'hidden lg:grid')}
        >
          {(['app', 'beheer'] as const).map((m) => (
            <button
              key={m}
              type="button"
              aria-pressed={effectiveMode === m}
              onClick={() => onModeChange(m)}
              className={cn(
                'py-xs transition-colors duration-fast',
                effectiveMode === m
                  ? 'bg-text-primary text-bg-primary'
                  : 'text-text-muted hover:text-text-primary',
              )}
            >
              {t(m === 'app' ? 'nav.modeApp' : 'nav.modeBeheer')}
            </button>
          ))}
        </div>
      )}
      {/* The icon rail (md, not lg) has no room for the two-button switch: one
          icon that flips the mode. The mobile drawer shows the full switch. */}
      {isOperator && !expanded && (
        <button
          type="button"
          onClick={() => onModeChange(nextMode)}
          aria-label={nextModeLabel}
          title={nextModeLabel}
          className={cn(linkCls({ isActive: false }), 'w-full mt-md lg:hidden')}
        >
          <ModeIcon className="w-4 h-4 shrink-0" aria-hidden />
        </button>
      )}

      <nav className="flex-1 overflow-auto pt-md">
        {groups.map((group) => (
          <div key={group.labelKey} className="mt-md first:mt-0">
            <p
              className={cn(
                'text-caption font-medium text-text-muted uppercase tracking-wide',
                groupCls,
              )}
              style={{ paddingLeft: '22px', paddingTop: '18px', paddingBottom: '6px' }}
            >
              {t(group.labelKey)}
            </p>
            <ul>
              {group.items.map((item) => {
                const Icon = item.icon;
                const badgeValue =
                  item.badgeKey ? badges[item.badgeKey] : 0;
                const locked = item.feature ? !hasFeature(item.feature) : false;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={linkCls}
                      onClick={onNavigate}
                      title={t(item.labelKey)}
                      // Still a real, clickable link -- the backend enforces
                      // the actual gate. This only sets expectations before
                      // the click, for sighted and screen-reader users alike.
                      aria-label={locked ? t('nav.lockedLabel', { label: t(item.labelKey) }) : undefined}
                    >
                      <Icon className="w-4 h-4 shrink-0" aria-hidden />
                      <span className={labelCls}>{t(item.labelKey)}</span>
                      {locked && (
                        <Lock className={cn('w-3.5 h-3.5 shrink-0 text-text-muted', badgeCls)} aria-hidden />
                      )}
                      {!locked && badgeValue > 0 && (
                        <span className={cn(item.badgeKey && HOT_BADGES.has(item.badgeKey) ? NAV_PILL_HOT : NAV_PILL, badgeCls)}>
                          {badgeValue}
                        </span>
                      )}
                      {!locked && item.tagKey && (
                        <span
                          className={cn(NAV_PILL, badgeCls)}
                        >
                          {t(item.tagKey)}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="mt-auto border-t border-border-subtle pt-md">
        <NavLink to="/settings" className={linkCls} onClick={onNavigate} title={t('nav.settings')}>
          <Settings className="w-4 h-4 shrink-0" aria-hidden />
          <span className={labelCls}>{t('nav.settings')}</span>
        </NavLink>
        {/* Mijn account is een overlay, geen pagina: het zijn drie schakelaars
            die je midden in je werk aanraakt, en je wilt daarna terug op de
            plek waar je was. Instellingen blijft wel een pagina -- daar zitten
            eigen dialogen in en de sectie hoort in een deelbare URL. */}
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            showAccount();
          }}
          title={t('nav.account')}
          className={cn(linkCls({ isActive: false }), 'w-full text-left')}
        >
          <UserRound className="w-4 h-4 shrink-0" aria-hidden />
          <span className={labelCls}>{t('nav.account')}</span>
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          title={t('auth.logout')}
          className={cn(
            'w-full flex items-center gap-sm py-sm text-body-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/50 transition-colors duration-fast',
            padCls,
          )}
        >
          <LogOut className="w-4 h-4 shrink-0" aria-hidden />
          <span className={expanded ? 'inline' : 'hidden lg:inline'}>{t('auth.logout')}</span>
        </button>
        {setupOpen > 0 && (
          <NavLink
            to="/?setup=1"
            className={cn('text-body-sm text-accent px-xl py-xs', groupCls)}
            onClick={onNavigate}
          >
            {t('nav.gettingStarted', { done: 4 - setupOpen, total: 4 })}
          </NavLink>
        )}
        {/* Support + status: one quiet line, no icons and no nav styling, so it
            reads as a footnote rather than a fifth destination. Hidden on the
            icon rail — two truncated words there would be noise. */}
        <div
          className={cn(
            'flex flex-wrap gap-x-sm text-caption text-text-muted pt-sm',
            expanded ? 'block px-xl' : 'hidden lg:flex px-xl',
          )}
        >
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="hover:text-text-secondary transition-colors duration-fast"
          >
            {t('nav.support')}
          </a>
          {STATUS_URL && (
            <a
              href={STATUS_URL}
              target="_blank"
              rel="noreferrer"
              className="hover:text-text-secondary transition-colors duration-fast"
            >
              {t('nav.status')}
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}

/** Mobile navigation drawer — full labelled sidebar slid in over a scrim.
 *  Only reachable below md (the in-grid sidebar is hidden there). */
function MobileNav({
  open,
  onClose,
  tenantName,
  badges,
  setupOpen,
  isOperator,
  mode,
  onModeChange,
  hasFeature,
}: {
  open: boolean;
  onClose: () => void;
  tenantName: string | null;
  badges: Record<BadgeKey, number>;
  setupOpen: number;
  isOperator: boolean;
  mode: NavMode;
  onModeChange: (mode: NavMode) => void;
  hasFeature: (feature: 'price_proposals' | 'dynamic_pricing') => boolean;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-y-0 left-0">
        <Sidebar
          tenantName={tenantName}
          badges={badges}
          setupOpen={setupOpen}
          isOperator={isOperator}
          mode={mode}
          onModeChange={onModeChange}
          hasFeature={hasFeature}
          expanded
          onNavigate={onClose}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="absolute top-md right-3 w-8 h-8 grid place-items-center rounded-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/60 transition-colors duration-fast"
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/**
 * F14: an exact match wins, else the longest registered prefix -- so a
 * detail route like `/admin/klanten/<id>` falls back to `/admin/klanten`
 * (`nav.adminCustomers`) instead of showing a blank crumb.
 */
function routeLabelFor(pathname: string): string | null {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  let best: string | null = null;
  for (const route of Object.keys(ROUTE_LABELS)) {
    if (pathname.startsWith(`${route}/`) && (best === null || route.length > best.length)) {
      best = route;
    }
  }
  return best ? ROUTE_LABELS[best] ?? null : null;
}

function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const labelKey = routeLabelFor(location.pathname);
  const crumb = labelKey ? t(labelKey) : '';

  // Per dashboard-v2 redesign (.ai/redesign/dashboard/contract.md § plumbing-
  // weghalen): top-bar shows the breadcrumb only. The "scrapers · live"
  // pill and `dev-…-pixel` build-tag are gone from the user-facing UI;
  // the BUILD_MARKER constant is dropped (was only consumed here).
  //
  // Mobile-only: on md+ the sidebar is visible, so the nav button is hidden and
  // the breadcrumb just repeats the eyebrow + title every page already renders.
  return (
    <div
      className="sticky top-0 z-5 flex md:hidden items-center gap-md border-b border-border-subtle bg-bg-primary/72 backdrop-blur-md"
      style={{ paddingTop: '14px', paddingBottom: '14px', paddingLeft: '28px', paddingRight: '28px' }}
      role="banner"
    >
      <button
        type="button"
        onClick={onOpenNav}
        aria-label={t('common.navigation')}
        className="-ml-2 w-9 h-9 grid place-items-center rounded-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/60 transition-colors duration-fast"
      >
        <Menu className="w-5 h-5" aria-hidden />
      </button>
      <div className="font-mono text-caption text-text-muted">
        <span className="text-text-primary">{crumb}</span>
      </div>
    </div>
  );
}
